import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import toast from 'react-hot-toast';
import Portal from '../components/Portal';
import { 
  Key, RefreshCw, Copy, Check, Terminal, 
  Shield, Code, ExternalLink, Info, Activity, Eye, EyeOff, Plus, Trash2, Globe, ToggleLeft, ToggleRight, Settings2, Save, X
} from 'lucide-react';

const DeveloperAPI = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [masterToken, setMasterToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMasterKey, setShowMasterKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(window.location.origin);
  
  // Limits Editing State
  const [editingLimits, setEditingLimits] = useState(null); // Key ID
  const [limitForm, setLimitForm] = useState({ daily_email_limit: '', daily_list_limit: '' });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/admin/api-keys');
      if (response.data.success) {
        setApiKeys(response.data.keys);
        setMasterToken(response.data.masterToken);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const createApiKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      setIsCreating(true);
      const response = await axios.post('/admin/api-keys', { name: newKeyName });
      if (response.data.success) {
        setApiKeys([response.data.key, ...apiKeys]);
        setNewKeyName('');
        setShowCreateModal(false);
        toast.success('API key created successfully');
        fetchApiKeys(); // Refresh to get all fields
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteApiKey = async (id) => {
    if (!window.confirm('Are you sure you want to delete this API key? Applications using this key will stop working immediately.')) {
      return;
    }

    try {
      const response = await axios.delete(`/admin/api-keys/${id}`);
      if (response.data.success) {
        setApiKeys(apiKeys.filter(key => key.id !== id));
        toast.success('API key deleted');
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const toggleKeyStatus = async (id) => {
    try {
      const response = await axios.patch(`/admin/api-keys/${id}/toggle`);
      if (response.data.success) {
        setApiKeys(apiKeys.map(key => 
          key.id === id ? { ...key, is_active: !key.is_active } : key
        ));
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const startEditingLimits = (key) => {
    setEditingLimits(key.id);
    setLimitForm({
      daily_email_limit: key.daily_email_limit || '',
      daily_list_limit: key.daily_list_limit || ''
    });
  };

  const saveLimits = async (id) => {
    try {
      const response = await axios.patch(`/admin/api-keys/${id}/limits`, limitForm);
      if (response.data.success) {
        setApiKeys(apiKeys.map(key => 
          key.id === id ? { 
            ...key, 
            daily_email_limit: limitForm.daily_email_limit === '' ? null : parseInt(limitForm.daily_email_limit),
            daily_list_limit: limitForm.daily_list_limit === '' ? null : parseInt(limitForm.daily_list_limit)
          } : key
        ));
        setEditingLimits(null);
        toast.success('Limits updated');
      }
    } catch (error) {
      toast.error('Failed to update limits');
    }
  };

  const regenerateMasterKey = async () => {
    if (!window.confirm('Are you sure you want to regenerate the Master API key?')) {
      return;
    }

    try {
      const response = await axios.post('/admin/api-token/regenerate');
      if (response.data.success) {
        setMasterToken(response.data.token);
        setShowMasterKey(true);
        toast.success('Master API key regenerated');
      }
    } catch (error) {
      toast.error('Failed to regenerate Master API key');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const apiDocs = [
    {
      title: 'Single Email Verification',
      method: 'POST',
      endpoint: '/api/v1/verify',
      description: 'Verify a single email address in real-time.',
      code: `curl -X POST ${currentDomain}/api/v1/verify \\
  -H "Authorization: Bearer ${apiKeys.find(k => k.is_active)?.api_key || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "test@example.com"}'`
    },
    {
      title: 'Batch Verification',
      method: 'POST',
      endpoint: '/api/v1/batch',
      description: 'Submit a list of emails for batch verification.',
      code: `curl -X POST ${currentDomain}/api/v1/batch \\
  -H "X-API-Token: ${apiKeys.find(k => k.is_active)?.api_key || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"emails": ["test1@example.com", "test2@example.com"]}'`
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Code className="text-brand-600 h-8 w-8" />
            Developer API
          </h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            Manage API keys with usage tracking, deactivation, and daily verification limits.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.open('https://smartemailverifier.com/docs', '_blank')}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm"
          >
            <ExternalLink className="h-4 w-4" />
            Full API Docs
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 transition-all shadow-lg shadow-brand-500/20"
          >
            <Plus className="h-4 w-4" />
            Create New Key
          </button>
        </div>
      </div>

      {/* Domain Status */}
      <div className="bg-blue-50 dark:bg-blue-500/5 border border-blue-100 dark:border-blue-500/10 rounded-2xl p-4 flex items-center gap-4">
        <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg text-blue-600 dark:text-blue-400">
          <Globe className="h-5 w-5" />
        </div>
        <div>
          <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Base API URL</span>
          <p className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-200">{currentDomain}</p>
        </div>
      </div>

      {/* API Keys Table */}
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800/60 overflow-hidden">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Key className="h-5 w-5 text-brand-600" />
            Active API Keys
          </h2>
          <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-bold rounded-full">
            {apiKeys.length} Keys
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider font-bold">
              <tr>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Name / Key</th>
                <th className="px-6 py-4">Daily Quotas (Used / Limit)</th>
                <th className="px-6 py-4">Last Activity</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
              {apiKeys.map((key) => (
                <tr key={key.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group ${!key.is_active ? 'opacity-60 bg-slate-50/30 dark:bg-slate-900/50' : ''}`}>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => toggleKeyStatus(key.id)}
                      className={`flex items-center gap-2 transition-colors ${key.is_active ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}
                    >
                      {key.is_active ? <ToggleRight className="h-8 w-8" /> : <ToggleLeft className="h-8 w-8" />}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-slate-900 dark:text-white">{key.name}</span>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-[10px] text-brand-600 dark:text-brand-400 font-mono">
                          {key.api_key.substring(0, 12)}...
                        </code>
                        <button 
                          onClick={() => copyToClipboard(key.api_key, key.id)}
                          className="p-1 text-slate-400 hover:text-brand-600 transition-colors"
                        >
                          {copiedKey === key.id ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {editingLimits === key.id ? (
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-400 w-12">Emails:</span>
                          <input 
                            type="number"
                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs w-20"
                            value={limitForm.daily_email_limit}
                            onChange={(e) => setLimitForm({ ...limitForm, daily_email_limit: e.target.value })}
                            placeholder="∞"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-400 w-12">Lists:</span>
                          <input 
                            type="number"
                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs w-20"
                            value={limitForm.daily_list_limit}
                            onChange={(e) => setLimitForm({ ...limitForm, daily_list_limit: e.target.value })}
                            placeholder="∞"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => saveLimits(key.id)} className="text-brand-600 text-[10px] font-bold flex items-center gap-1"><Save className="h-3 w-3" /> Save</button>
                          <button onClick={() => setEditingLimits(null)} className="text-slate-400 text-[10px] font-bold flex items-center gap-1"><X className="h-3 w-3" /> Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5 min-w-[150px]">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-500 uppercase">Emails</span>
                          <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">
                            {key.emails_used_today} / {key.daily_email_limit || '∞'}
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1 overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${key.emails_used_today >= (key.daily_email_limit || Infinity) ? 'bg-red-500' : 'bg-brand-500'}`}
                            style={{ width: `${key.daily_email_limit ? Math.min(100, (key.emails_used_today / key.daily_email_limit) * 100) : 0}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-slate-500 uppercase">Lists</span>
                          <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">
                            {key.lists_used_today} / {key.daily_list_limit || '∞'}
                          </span>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                        <Activity className="h-3 w-3 text-brand-500" />
                        {formatDate(key.last_used)}
                      </span>
                      <span className="text-[10px] text-slate-400">Created {new Date(key.created_at).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => startEditingLimits(key)}
                        className="p-2 text-slate-400 hover:text-brand-600 transition-colors"
                        title="Edit Limits"
                      >
                        <Settings2 className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => deleteApiKey(key.id)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                        title="Delete API Key"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {apiKeys.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    No custom API keys created yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Master Key Compatibility Section */}
      <div className="bg-slate-50 dark:bg-slate-800/20 rounded-3xl p-8 border border-slate-200 dark:border-slate-800/60">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
              <Shield className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Master API Key (Superuser)</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
                This key has no daily limits and cannot be deactivated. Use it only for critical system-level integrations.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex-grow md:flex-grow-0 font-mono bg-white dark:bg-slate-950 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800 text-sm">
              {showMasterKey ? masterToken : '••••••••••••••••'}
            </div>
            <button 
              onClick={() => setShowMasterKey(!showMasterKey)}
              className="p-2 text-slate-400 hover:text-brand-600 transition-colors"
            >
              {showMasterKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
            <button 
              onClick={() => copyToClipboard(masterToken, 'master')}
              className="p-2 text-slate-400 hover:text-brand-600 transition-colors"
            >
              {copiedKey === 'master' ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
            </button>
            <button 
              onClick={regenerateMasterKey}
              className="p-2 text-slate-400 hover:text-brand-600 transition-colors"
              title="Regenerate Master Key"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* API Quick Start */}
      <div className="space-y-6">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Terminal className="text-brand-600 h-6 w-6" />
          Quick Start Examples
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {apiDocs.map((doc, index) => (
            <div key={index} className="bg-white dark:bg-slate-900 rounded-3xl shadow-lg border border-slate-200 dark:border-slate-800/60 overflow-hidden flex flex-col">
              <div className="p-8 flex-grow">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-slate-900 dark:text-white">{doc.title}</h4>
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 text-xs font-bold rounded-full border border-green-200/50 dark:border-green-500/20">
                    {doc.method}
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{doc.description}</p>
                <div className="relative group">
                  <pre className="bg-slate-950 text-slate-300 p-6 rounded-2xl font-mono text-xs overflow-x-auto border border-slate-800 shadow-inner">
                    <code>{doc.code}</code>
                  </pre>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(doc.code);
                      toast.success('Code copied');
                    }}
                    className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-slide-up border border-slate-200 dark:border-slate-800">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800/60">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Create New API Key</h3>
            </div>
            <form onSubmit={createApiKey} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Key Name</label>
                <input 
                  type="text"
                  placeholder="e.g. Website Integration, Mobile App"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 transition-all outline-none"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-brand-600 hover:bg-brand-700 transition-all shadow-lg shadow-brand-500/20 disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div></Portal>
      )}
    </div>
  );
};

export default DeveloperAPI;
